import { FlowConfig } from '../types/flow';

export class FlowLoader {
  private static instance: FlowLoader;
  private flowCache: Map<string, FlowConfig> = new Map();

  private constructor() {}

  static getInstance(): FlowLoader {
    if (!FlowLoader.instance) {
      FlowLoader.instance = new FlowLoader();
    }
    return FlowLoader.instance;
  }

  async loadFlow(clientId: string): Promise<FlowConfig> {
    if (this.flowCache.has(clientId)) {
      return this.flowCache.get(clientId)!;
    }

    try {
      const clientFlow = await this.loadFlowFile(clientId);
      if (clientFlow && this.validateFlowConfig(clientFlow)) {
        this.flowCache.set(clientId, clientFlow);
        return clientFlow;
      }
    } catch (error) {
      console.warn(`Failed to load flow for client ${clientId}:`, error);
    }

    try {
      const defaultFlow = await this.loadFlowFile('default');
      if (defaultFlow && this.validateFlowConfig(defaultFlow)) {
        this.flowCache.set(clientId, defaultFlow);
        return defaultFlow;
      }
    } catch (error) {
      console.error('Failed to load default flow:', error);
    }

    throw new Error(
      `Flow configuration not found for client "${clientId}" and default flow is missing or invalid. ` +
      `Please ensure /themes/default/flow.json exists and is valid.`
    );
  }

  private async loadFlowFile(clientId: string): Promise<FlowConfig | null> {
    try {
      const response = await fetch(`/themes/CarelonRx/${clientId}/flow.json`);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  private validateFlowConfig(config: FlowConfig): boolean {
    if (!config || typeof config !== 'object') {
      console.error('Flow config is not a valid object');
      return false;
    }

    if (!config.client || !config.client.name || !config.client.flowVersion) {
      console.error('Flow config missing required client information');
      return false;
    }

    if (!config.flow || !Array.isArray(config.flow.steps)) {
      console.error('Flow config missing steps array');
      return false;
    }

    if (config.flow.steps.length === 0) {
      console.error('Flow config has no steps defined');
      return false;
    }

    for (const step of config.flow.steps) {
      if (!step.id || !step.component) {
        console.error('Flow step missing required id or component', step);
        return false;
      }
      if (typeof step.required !== 'boolean') {
        console.error('Flow step missing required boolean field', step);
        return false;
      }
      if (!Array.isArray(step.skipConditions)) {
        console.error('Flow step skipConditions must be an array', step);
        return false;
      }
    }

    if (!config.flow.settings || typeof config.flow.settings !== 'object') {
      console.error('Flow config missing settings object');
      return false;
    }

    return true;
  }

  clearCache(): void {
    this.flowCache.clear();
  }
}
